import {
  getDestination,
  getIntegrationDestination,
} from '@/features/interactions/addToCalendar/destinations/registry';
import type { IntegrationPlatform } from '@/features/interactions/addToCalendar/types';
import { BulkExportControl } from '@/features/interactions/multiExport/components/BulkExportControl/BulkExportControl';
import type { PlatformTypes } from '@/types/gql.generated';
import { Button, Flex, Text } from '@/ui';
import { getName } from '@/utils/platformLib';
import { CalendarSubscribeControl } from './CalendarSubscribeControl';

type Props = {
  platform: PlatformTypes | null;
  /** Already filtered to the visible range by the caller. */
  selectedEventIds: string[];
  isDrawerOpen: boolean;
  setIsDrawerOpen: (open: boolean) => void;
  onSelectAll: () => void;
  onDeselectAll: () => void;
};

// Primary action for the chosen destination — below the chips on desktop, pinned
// to the bottom on mobile. Callers don't render this for weblink (each row's plus
// is the action) or feed. Integrations bulk-select.
export const DestinationActions = ({
  platform,
  selectedEventIds,
  isDrawerOpen,
  setIsDrawerOpen,
  onSelectAll,
  onDeselectAll,
}: Props) => {
  if (!platform) return null;

  const selectedCount = selectedEventIds.length;

  if (selectedCount === 0) {
    if (getDestination(platform)?.addsAllAsFeed) {
      // An empty override selects the whole-collection path.
      return (
        <BulkExportControl
          addAllLabel={`Add all to ${getName(platform)}`}
          ctaVariant="ahDark"
          isDrawerOpen={isDrawerOpen}
          selectedEventIds={[]}
          selectedPlatform={platform}
          setIsDrawerOpen={setIsDrawerOpen}
          onSuccess={onDeselectAll}
        />
      );
    }

    const integration = getIntegrationDestination(platform)?.integration;
    if (integration?.supportsSubscription) {
      // No Select all here: it reads as a competing way to do what Subscribe
      // to All does. A subset is built by ticking rows instead. Destinations
      // without a subscription keep it below, where it is the only bulk action.
      return (
        <CalendarSubscribeControl platform={platform as IntegrationPlatform} />
      );
    }

    return (
      <Button size="ah" variant="ahDark" w="100%" onClick={onSelectAll}>
        Select all
      </Button>
    );
  }

  return (
    <Flex direction="column" gap={2.5}>
      <Flex align="baseline" justify="space-between">
        <Text color="ink.muted" fontSize="ah-status">
          {selectedCount} {selectedCount === 1 ? 'event' : 'events'} selected
        </Text>
        <Text color="ink.strong" fontSize="ah-status" fontWeight={700}>
          {getName(platform)}
        </Text>
      </Flex>
      <BulkExportControl
        ctaVariant="ahDark"
        isDrawerOpen={isDrawerOpen}
        selectedEventIds={selectedEventIds}
        selectedPlatform={platform}
        setIsDrawerOpen={setIsDrawerOpen}
        onSuccess={onDeselectAll}
      />
    </Flex>
  );
};
